import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CostCentersService } from '../../../services/cost-centers.service';
import { StaffService } from '../../../services/staff.service';
import { AuthService } from '../../../services/auth.service';
import { ErrorService } from '../../../shared/services/error.service';
import {
  CostCenterBudgetProposal,
  getBudgetProposalStatusBadgeClass,
  getBudgetProposalStatusLabel,
  isBudgetProposalPending
} from '../../../models/CostCenterBudgetProposal';
import { formatBrazilDateTime, toBrazilOffsetIsoString } from '../../../utils/datetime.utils';

type ReviewModalKind = 'approve' | 'reject';

@Component({
  selector: 'app-cost-center-budget-proposal-view',
  templateUrl: './cost-center-budget-proposal-view.component.html',
  styleUrls: ['./cost-center-budget-proposal-view.component.css']
})
export class CostCenterBudgetProposalViewComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  proposal: CostCenterBudgetProposal | null = null;
  proposalId: number | null = null;
  requestedByName: string | null = null;
  reviewerName: string | null = null;
  isLoading = false;
  error: string | null = null;

  reviewModalKind: ReviewModalKind | null = null;
  reviewComments = '';
  isReviewSubmitting = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private costCentersService: CostCentersService,
    private staffService: StaffService,
    private authService: AuthService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = Number(params.get('id'));
      if (!id) {
        this.error = 'Proposta não informada.';
        return;
      }
      this.proposalId = id;
      this.loadProposal();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProposal(): void {
    if (this.proposalId == null) {
      return;
    }
    this.isLoading = true;
    this.error = null;
    this.requestedByName = null;
    this.reviewerName = null;

    this.costCentersService
      .getBudgetProposalById(this.proposalId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (p) => {
          this.proposal = p;
          this.isLoading = false;
          this.loadRequesterName(p.requestedByStaffId);
          this.loadReviewerName(p.reviewedByStaffId);
        },
        error: () => {
          this.isLoading = false;
          this.error = 'Não foi possível carregar a proposta de orçamento.';
        }
      });
  }

  goBack(): void {
    this.router.navigate(['/financial/budget-proposals']);
  }

  canReviewProposal(): boolean {
    return isBudgetProposalPending(this.proposal?.status);
  }

  openReviewModal(kind: ReviewModalKind): void {
    if (!this.canReviewProposal() || this.isReviewSubmitting) {
      return;
    }
    this.reviewModalKind = kind;
    this.reviewComments = '';
  }

  closeReviewModal(): void {
    if (this.isReviewSubmitting) {
      return;
    }
    this.reviewModalKind = null;
    this.reviewComments = '';
  }

  getReviewModalTitle(): string {
    return this.reviewModalKind === 'approve' ? 'Aprovar proposta' : 'Recusar proposta';
  }

  canSubmitReview(): boolean {
    return (this.reviewComments || '').trim().length >= 3;
  }

  submitReview(): void {
    if (!this.reviewModalKind || !this.proposalId || !this.canSubmitReview() || this.isReviewSubmitting) {
      return;
    }
    const reviewedByStaffId = this.authService.getStaffIdFromToken();
    if (reviewedByStaffId == null || reviewedByStaffId <= 0) {
      this.errorService.showErrorMessage(
        'Não foi possível identificar o gestor logado. Faça login novamente.'
      );
      return;
    }

    const payload = {
      reviewedByStaffId,
      reviewComments: this.reviewComments.trim(),
      reviewedAt: toBrazilOffsetIsoString()
    };
    const request$ =
      this.reviewModalKind === 'approve'
        ? this.costCentersService.approveBudgetProposal(this.proposalId, payload)
        : this.costCentersService.rejectBudgetProposal(this.proposalId, payload);

    this.isReviewSubmitting = true;
    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (p) => {
        const wasApprove = this.reviewModalKind === 'approve';
        this.isReviewSubmitting = false;
        this.reviewModalKind = null;
        this.reviewComments = '';
        this.proposal = p;
        this.loadReviewerName(p.reviewedByStaffId);
        this.errorService.showSuccessMessage(
          wasApprove ? 'Proposta aprovada com sucesso.' : 'Proposta recusada com sucesso.'
        );
      },
      error: () => {
        this.isReviewSubmitting = false;
      }
    });
  }

  getCostCenterLabel(): string {
    const p = this.proposal;
    if (!p) {
      return '—';
    }
    const name = (p.costCenterName || '').trim();
    const code = (p.costCenterCode || '').trim();
    if (code && name) {
      return `${code} — ${name}`;
    }
    return name || code || '—';
  }

  getStatusLabel(): string {
    return getBudgetProposalStatusLabel(this.proposal?.status);
  }

  getStatusBadgeClass(): string {
    return getBudgetProposalStatusBadgeClass(this.proposal?.status);
  }

  formatMoney(value: number | undefined | null): string {
    if (value == null || !Number.isFinite(Number(value))) {
      return '—';
    }
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value));
  }

  formatDateTime(value?: string): string {
    return formatBrazilDateTime(value);
  }

  private loadRequesterName(staffId?: number): void {
    if (staffId == null || staffId <= 0) {
      return;
    }
    this.staffService
      .getStaffById(staffId, { suppressErrorToast: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (s) => {
          this.requestedByName = (s.FullName || '').trim() || null;
        }
      });
  }

  private loadReviewerName(staffId?: number): void {
    if (staffId == null || staffId <= 0) {
      this.reviewerName = null;
      return;
    }
    this.staffService
      .getStaffById(staffId, { suppressErrorToast: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (s) => {
          this.reviewerName = (s.FullName || '').trim() || null;
        }
      });
  }
}
